/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { App, DirectiveBinding, ObjectDirective } from 'vue';

/** ========= 类型定义 ========= */
export interface LazyItemBinding {
  /** 目标大图地址（必填） */
  src: string;
  /** 加载中占位图（可选） */
  loading?: string;
  /** 加载失败显示图（可选） */
  error?: string;
  /** 背景图模式：true 表示把 src 设置到 style.backgroundImage */
  background?: boolean;
  /** 单元素覆盖全局配置 */
  options?: Partial<LazyPreloadOptions>;
  /** 元素加载完成（单元素回调） */
  onLoaded?: (el: HTMLElement) => void;
  /** 元素加载失败 */
  onError?: (el: HTMLElement, err: unknown) => void;
}

export interface LazyPreloadOptions {
  /** 全局加载中占位图 */
  loading?: string;
  /** 全局加载失败显示图 */
  error?: string;
  /** 是否输出日志 */
  log: boolean;
  /** 进入视口判定阈值 */
  threshold: number;               // 0~1
  /** 最大并发 */
  maxConcurrency: number;          // e.g. 4~8
  /** 空闲批量大小 */
  batchSize: number;               // e.g. 8~20
  /** 最大重试次数 */
  maxRetries: number;              // e.g. 2~3
  /** 初始退避(ms) */
  retryBaseDelay: number;          // e.g. 300
  /** 是否在 document 不可见时暂停预加载 */
  pauseWhenHidden: boolean;
  /** 是否尊重 Data Saver（Save-Data）与 2g/effectiveType */
  respectNetworkCondition: boolean;
}

/** 允许的指令绑定：字符串（src）或对象（详参） */
type BindingValue = string | LazyItemBinding;

/** ========= 环境能力 ========= */
declare global {
  // 轻量声明，避免装类型库
  interface Window {
    requestIdleCallback?: (cb: IdleRequestCallback, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  }
}

const hasRIC = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function';

/** ========= 工具 ========= */
const loadedCache = new Map<string, boolean>(); // 同 src 去重
const controllerMap = new WeakMap<HTMLElement, AbortController>();
const loadingStateMap = new WeakMap<HTMLElement, 'idle' | 'loading' | 'loaded' | 'error'>(); // 元素加载状态
const elementSrcMap = new WeakMap<HTMLElement, string>(); // 元素当前绑定的 src

const now = () => performance?.now?.() ?? Date.now();

/** 根据网络状况动态调整并发/批量 */
function tuneByNetwork(base: Pick<LazyPreloadOptions, 'maxConcurrency' | 'batchSize'>): Pick<LazyPreloadOptions, 'maxConcurrency' | 'batchSize'> {
  try {
    const anyNav = (navigator as any);
    const conn = anyNav?.connection || anyNav?.mozConnection || anyNav?.webkitConnection;
    const saveData = !!conn?.saveData || (anyNav?.connection?.saveData === true);
    const type: string | undefined = conn?.effectiveType; // 'slow-2g' | '2g' | '3g' | '4g'
    let maxC = base.maxConcurrency;
    let batch = base.batchSize;
    if (saveData) { maxC = Math.max(2, Math.floor(maxC / 2)); batch = Math.max(2, Math.floor(batch / 2)); }
    if (type === '2g' || type === 'slow-2g') { maxC = Math.max(2, Math.floor(maxC / 2)); batch = Math.max(2, Math.floor(batch / 2)); }
    if (type === '3g') { maxC = Math.max(3, Math.floor((maxC * 2) / 3)); batch = Math.max(3, Math.floor((batch * 2) / 3)); }
    return { maxConcurrency: maxC, batchSize: batch };
  } catch { return base; }
}

/** ========= 调度器（单例） ========= */
interface QueueItem {
  el: HTMLElement;
  src: string;
  background: boolean;
  loading?: string;
  error?: string;
  options: LazyPreloadOptions;
  retries: number;
  enqueuedAt: number;
  // 元素回调
  onLoaded?: (el: HTMLElement) => void;
  onError?: (el: HTMLElement, err: unknown) => void;
}

class LazyScheduler {
  private queue: QueueItem[] = [];
  private observing = new WeakMap<HTMLElement, IntersectionObserver>();
  private running = 0;
  private rafId = 0;
  private idleId = 0;

  constructor(private globalOptions: LazyPreloadOptions) {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** 注册元素到懒加载观察 */
  observe(el: HTMLElement, item: QueueItem) {
    // 检查元素状态，避免重复观察
    const state = loadingStateMap.get(el);
    if (state === 'loading' || state === 'loaded') {
      return; // 已经在加载中或已加载，不再重复观察
    }

    // 存储元素当前的 src
    elementSrcMap.set(el, item.src);

    // 如果图片已在全局缓存中，直接应用，不需要观察
    if (loadedCache.get(item.src)) {
      if (item.options.log) {
        console.log(`[v-lazy-preload] 直接使用全局缓存: ${item.src}`);
      }
      this.applySrcFromCache(item);
      return;
    }

    // 标记为空闲状态
    loadingStateMap.set(el, 'idle');

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          // 再次检查状态，避免重复触发
          const currentState = loadingStateMap.get(el);
          const obs = this.observing.get(el);

          if (currentState === 'loading' || currentState === 'loaded') {
            // 已经在处理或已完成，立即停止观察
            if (obs) {
              obs.unobserve(el);
              obs.disconnect();
              this.observing.delete(el);
            }
            return;
          }

          // 真正进入视口 -> 立刻高优先级执行
          this.loadNow(item);

          // 执行后立即停止观察
          if (obs) {
            obs.unobserve(el);
            obs.disconnect();
            this.observing.delete(el);
          }
        }
      }
    }, { threshold: item.options.threshold });

    io.observe(el);
    this.observing.set(el, io);
    // 同时加入空闲队列（只要不在视口就加载）
    this.enqueue(item);
  }

  /** 取消监听与队列 */
  unobserve(el: HTMLElement) {
    const obs = this.observing.get(el);
    obs?.unobserve(el);
    obs?.disconnect?.();
    this.observing.delete(el);
    // 从队列移除
    this.queue = this.queue.filter(q => q.el !== el);
    // 取消未完成的加载
    const ctl = controllerMap.get(el);
    ctl?.abort();
    controllerMap.delete(el);
    // 清理状态
    loadingStateMap.delete(el);
    // 清理 src 缓存
    elementSrcMap.delete(el);
  }

  /** 入队并触发一次调度 */
  private enqueue(item: QueueItem) {
    const state = loadingStateMap.get(item.el);
    // 只有空闲状态才入队，避免重复
    if (state !== 'idle') {
      return;
    }
    this.queue.push(item);
    this.schedule();
  }

  /** 调度：空闲时做批量加载 */
  private schedule = () => {
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.pump(); // 有并发余量先吃一口
      this.scheduleIdle();
    });
  };

  private scheduleIdle() {
    if (hasRIC) {
      if (this.idleId) window.cancelIdleCallback!(this.idleId);
      this.idleId = window.requestIdleCallback!(() => this.pumpIdle(), { timeout: 800 });
    } else {
      // 退化
      setTimeout(() => this.pumpIdle(), 300);
    }
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.schedule();
    }
  };

  /** 立刻加载（进入视口的项最高优先） */
  private loadNow(item: QueueItem) {
    const state = loadingStateMap.get(item.el);
    // 已经在加载或已加载，不再处理
    if (state === 'loading' || state === 'loaded') {
      return;
    }

    // 放到队首，并尝试立即执行
    const i = this.queue.findIndex(q => q.el === item.el);
    if (i !== -1) this.queue.splice(i, 1);
    this.queue.unshift(item);
    this.pump(true);
  }

  /** 并发泵：抓取队列前若干项 */
  private pump(force = false) {
    if (document.visibilityState === 'hidden' && this.globalOptions.pauseWhenHidden) return;

    // 动态并发/批量（仅作上限保护）
    const tuned = this.globalOptions.respectNetworkCondition
      ? tuneByNetwork({ maxConcurrency: this.globalOptions.maxConcurrency, batchSize: this.globalOptions.batchSize })
      : { maxConcurrency: this.globalOptions.maxConcurrency, batchSize: this.globalOptions.batchSize };

    while (this.running < tuned.maxConcurrency && this.queue.length > 0) {
      const next = this.queue.shift()!;

      // 检查元素状态，跳过已加载或加载中的元素
      const state = loadingStateMap.get(next.el);
      if (state === 'loading' || state === 'loaded') {
        continue;
      }

      // 如果元素已经不在文档流，跳过
      if (!document.body.contains(next.el)) continue;

      this.running++;
      this.startLoad(next)
        .catch(() => { /* 错误在内部处理 */ })
        .finally(() => {
          this.running = Math.max(0, this.running - 1);
          if (force) this.pump(true);
        });
      if (!force && this.running >= tuned.maxConcurrency) break;
    }
  }

  /** 空闲批量泵（小口吃） */
  private pumpIdle() {
    if (document.visibilityState === 'hidden' && this.globalOptions.pauseWhenHidden) return;

    const tuned = this.globalOptions.respectNetworkCondition
      ? tuneByNetwork({ maxConcurrency: this.globalOptions.maxConcurrency, batchSize: this.globalOptions.batchSize })
      : { maxConcurrency: this.globalOptions.maxConcurrency, batchSize: this.globalOptions.batchSize };

    const take = Math.min(tuned.batchSize, this.queue.length);
    if (take <= 0) return;

    for (let i = 0; i < take; i++) {
      if (this.running >= tuned.maxConcurrency) break;
      const item = this.queue.shift();
      if (!item) break;

      // 检查元素状态，跳过已加载或加载中的元素
      const state = loadingStateMap.get(item.el);
      if (state === 'loading' || state === 'loaded') {
        continue;
      }

      this.running++;
      this.startLoad(item)
        .catch(() => { /* handled */ })
        .finally(() => { this.running = Math.max(0, this.running - 1); });
    }
  }

  /** 实际加载 */
  private async startLoad(q: QueueItem): Promise<void> {
    // 检查元素状态，避免重复加载
    const state = loadingStateMap.get(q.el);
    if (state === 'loading' || state === 'loaded') {
      return; // 已经在加载中或已加载完成
    }

    // 标记为加载中
    loadingStateMap.set(q.el, 'loading');

    // 已加载过（全局缓存），直接赋值
    if (loadedCache.get(q.src)) {
      if (q.options.log) {
        console.log(`[v-lazy-preload] 使用缓存: ${q.src}`);
      }
      this.applySrc(q);
      q.onLoaded?.(q.el);
      q.el.dispatchEvent(new CustomEvent('lazy:loaded'));
      loadingStateMap.set(q.el, 'loaded'); // 标记为已加载

      // 立即清理 IntersectionObserver，防止重复触发
      const obs = this.observing.get(q.el);
      if (obs) {
        obs.unobserve(q.el);
        obs.disconnect();
        this.observing.delete(q.el);
      }

      return;
    }

    // Abort 控制
    const ctl = new AbortController();
    controllerMap.set(q.el, ctl);

    if (q.options.log) {
      console.log(`[v-lazy-preload] 开始加载: ${q.src}`);
    }

    try {
      await this.loadImage(q.src, ctl.signal);
      loadedCache.set(q.src, true);
      this.applySrc(q);
      q.onLoaded?.(q.el);
      q.el.dispatchEvent(new CustomEvent('lazy:loaded'));
      loadingStateMap.set(q.el, 'loaded'); // 标记为已加载

      // 立即清理 IntersectionObserver，防止重复触发
      const obs = this.observing.get(q.el);
      if (obs) {
        obs.unobserve(q.el);
        obs.disconnect();
        this.observing.delete(q.el);
      }

      if (q.options.log) {
        console.log(`[v-lazy-preload] 加载成功: ${q.src}`);
      }
    } catch (err) {
      if ((err as any)?.name === 'AbortError') {
        if (q.options.log) {
          console.log(`[v-lazy-preload] 加载取消: ${q.src}`);
        }
        q.el.dispatchEvent(new CustomEvent('lazy:cancel'));
        loadingStateMap.set(q.el, 'idle'); // 取消后恢复空闲状态
        return;
      }
      if (q.retries < q.options.maxRetries) {
        q.retries++;
        if (q.options.log) {
          console.warn(`[v-lazy-preload] 加载失败，重试 ${q.retries}/${q.options.maxRetries}: ${q.src}`, err);
        }
        // 指数退避
        const delay = q.options.retryBaseDelay * Math.pow(2, q.retries - 1);
        await new Promise(r => setTimeout(r, delay));
        // 重新排入队尾
        q.enqueuedAt = now();
        loadingStateMap.set(q.el, 'idle'); // 重置为空闲，允许重试
        this.queue.push(q);
        this.schedule();
      } else {
        // 达到最大重试次数，显示 error 图
        if (q.options.log) {
          console.error(`[v-lazy-preload] 加载失败，已达最大重试次数: ${q.src}`, err);
        }
        if (q.error) {
          this.applyErrorImg(q);
        }
        q.onError?.(q.el, err);
        q.el.dispatchEvent(new CustomEvent('lazy:error', { detail: err }));
        loadingStateMap.set(q.el, 'error'); // 标记为错误状态

        // 清理 IntersectionObserver
        const obs = this.observing.get(q.el);
        if (obs) {
          obs.unobserve(q.el);
          obs.disconnect();
          this.observing.delete(q.el);
        }
      }
    } finally {
      controllerMap.delete(q.el);
    }
  }

  private applySrc(q: QueueItem) {
    if (q.background) {
      (q.el as HTMLElement).style.backgroundImage = `url("${q.src}")`;
      q.el.classList.add('lazy-loaded');
    } else {
      const img = q.el as HTMLImageElement;
      img.src = q.src;
      q.el.classList.add('lazy-loaded');
    }
  }

  /** 从缓存直接应用图片（跳过加载流程） */
  private applySrcFromCache(q: QueueItem) {
    this.applySrc(q);
    loadingStateMap.set(q.el, 'loaded');
    q.onLoaded?.(q.el);
    q.el.dispatchEvent(new CustomEvent('lazy:loaded'));
  }

  private applyErrorImg(q: QueueItem) {
    if (q.background) {
      (q.el as HTMLElement).style.backgroundImage = `url("${q.error}")`;
      q.el.classList.add('lazy-error');
    } else {
      const img = q.el as HTMLImageElement;
      img.src = q.error!;
      q.el.classList.add('lazy-error');
    }
  }

  private loadImage(src: string, signal: AbortSignal): Promise<void> {
    // 用 <img> 解码，享受浏览器缓存；也可替换为 fetch + blob URL
    return new Promise((resolve, reject) => {
      const img = new Image();
      const onAbort = () => { img.src = ''; reject(new DOMException('Aborted', 'AbortError')); };

      const cleanup = () => {
        signal.removeEventListener('abort', onAbort);
        img.onload = null;
        img.onerror = null;
      };

      img.onload = async () => {
        try { await (img.decode?.() ?? void 0); } catch { /* 某些浏览器 decode 可能抛错，忽略 */ }
        cleanup();
        resolve();
      };
      img.onerror = (e) => { cleanup(); reject(e); };

      signal.addEventListener('abort', onAbort, { once: true });
      img.src = src;
    });
  }

  /** 更新单项（用于 updated 钩子） */
  update(el: HTMLElement, binding: LazyItemBinding | string) {
    // 提取新的 src
    const newSrc = typeof binding === 'string' ? binding : binding.src;
    // 获取旧的 src
    const oldSrc = elementSrcMap.get(el);

    // 只有当 src 变化时才重新观察
    if (oldSrc === newSrc) {
      return; // src 未变化，不做任何处理
    }

    // src 变化了，取消旧的，重新入队
    this.unobserve(el);
    const item = normalizeBinding(el, binding, this.globalOptions);
    this.observe(el, item);
  }

  /** 销毁 */
  dispose() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.queue = [];
    this.observing = new WeakMap();
  }
}

/** ========= 默认配置 ========= */
const DEFAULTS: LazyPreloadOptions = {
  log: false,
  threshold: 0.01,
  maxConcurrency: 6,
  batchSize: 12,
  maxRetries: 2,
  retryBaseDelay: 300,
  pauseWhenHidden: true,
  respectNetworkCondition: true,
};

/** 单例调度器（支持多 app 安装：用闭包工厂） */
function createScheduler(defaults: LazyPreloadOptions) {
  return new LazyScheduler(defaults);
}

/** 解析 binding -> QueueItem */
function normalizeBinding(el: HTMLElement, binding: BindingValue, global: LazyPreloadOptions): QueueItem {
  const isStr = typeof binding === 'string';
  const obj = isStr ? { src: binding as string } : (binding as LazyItemBinding);
  const opts = { ...global, ...(obj.options ?? {}) };

  // 获取最终的 loading 和 error 图
  const loadingImg = obj.loading ?? opts.loading;
  const errorImg = obj.error ?? opts.error;

  // 加载中占位图：立即设置
  if (loadingImg) {
    if (obj.background) {
      el.setAttribute('style', `${el.getAttribute('style') ?? ''};background-image:url("${loadingImg}");background-size:cover;background-position:center;`);
    } else {
      (el as HTMLImageElement).src = loadingImg;
    }
  }

  return {
    el,
    src: obj.src,
    loading: loadingImg,
    error: errorImg,
    background: !!obj.background,
    options: opts,
    retries: 0,
    enqueuedAt: now(),
    onLoaded: obj.onLoaded,
    onError: obj.onError,
  };
}

/** ========= 指令工厂 & 插件 ========= */
export function createLazyPreloadDirective(globalOptions?: Partial<LazyPreloadOptions>) {
  const merged = { ...DEFAULTS, ...(globalOptions ?? {}) };
  const scheduler = createScheduler(merged);

  const directive: ObjectDirective<HTMLElement, BindingValue> = {
    mounted(el, binding: DirectiveBinding<BindingValue>) {
      const item = normalizeBinding(el, binding.value, merged);
      scheduler.observe(el, item);
    },
    updated(el, binding) {
      // 若用户更新了 src/配置，重新接入
      scheduler.update(el, binding.value);
    },
    unmounted(el) {
      scheduler.unobserve(el);
    },
  };

  return directive;
}

/**
 * 作为 Vue 插件使用：app.use(...)
 *
 * @example
 * // 基础使用
 * app.use(LazyPreloadPlugin, {
 *   loading: 'loading.gif',
 *   error: 'error.jpg',
 *   log: false
 * });
 *
 * // 模板中使用
 * <img v-lazy-preload="imageUrl" />
 * <img v-lazy-preload="{ src: imageUrl, loading: customLoading }" />
 * <div v-lazy-preload-bg="{ src: bgUrl, background: true }" />
 */
export default {
  install(app: App, globalOptions?: Partial<LazyPreloadOptions>) {
    const dir = createLazyPreloadDirective(globalOptions);
    app.directive('lazy-preload', dir);
  },
};
